/**
 * Warehouse Management Page
 *
 * Main page for managing warehouse movements (inbound/outbound)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  RefreshCw,
  Download,
  Truck,
  Package,
  ArrowLeftRight,
  Loader2,
  X,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import warehouseService from '../services/warehouseService';
import InboundList from '../components/warehouse/InboundList';
import OutboundList from '../components/warehouse/OutboundList';
import CreateOutboundDialog from '../components/warehouse/CreateOutboundDialog';
import CreateInboundDialog from '../components/warehouse/CreateInboundDialog';

function WarehouseManagementPage() {
  const [user, setUser] = useState(null);
  const [tabValue, setTabValue] = useState('outbound');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createInboundDialogOpen, setCreateInboundDialogOpen] = useState(false);

  // Load user info on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await warehouseService.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics, refreshTrigger]);

  const handleTabChange = (value) => {
    setTabValue(value);
    setError(null);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    loadStatistics();
  };

  const handleCreateOutbound = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateInbound = () => {
    setCreateInboundDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    setCreateInboundDialogOpen(false);
    handleRefresh();
  };

  const handleExport = async (type) => {
    try {
      setLoading(true);
      await warehouseService.exportMovements({
        movement_type: type,
        template_type: type
      });
    } catch (err) {
      setError(`导出失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Check user permissions
  const canCreateMovement = user && ['admin', 'warehouse_manager', 'sales_person'].includes(user.role);
  const canConfirmMovement = user && ['admin', 'warehouse_manager', 'sales_person'].includes(user.role);

  return (
    <Layout title="仓库管理" activeTab="warehouse">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">仓库管理</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              title="刷新"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {canCreateMovement && tabValue === 'outbound' && (
              <Button onClick={handleCreateOutbound}>
                <Plus className="mr-2 h-4 w-4" />
                创建出库单
              </Button>
            )}
            {canCreateMovement && tabValue === 'inbound' && (
              <Button onClick={handleCreateInbound}>
                <Plus className="mr-2 h-4 w-4" />
                创建入库单
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">今日操作</p>
                    <p className="text-3xl font-bold">{statistics.today || 0}</p>
                  </div>
                  <Truck className="h-10 w-10 text-primary opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">入库单</p>
                    <p className="text-3xl font-bold">{statistics.by_type?.inbound || 0}</p>
                  </div>
                  <Package className="h-10 w-10 text-success opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">出库单</p>
                    <p className="text-3xl font-bold">{statistics.by_type?.outbound || 0}</p>
                  </div>
                  <Truck className="h-10 w-10 text-warning opacity-30" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">待处理</p>
                    <p className="text-3xl font-bold text-warning">
                      {statistics.by_status?.pending || 0}
                    </p>
                  </div>
                  <ArrowLeftRight className="h-10 w-10 text-warning opacity-30" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content Tabs */}
        <Card>
          <Tabs value={tabValue} onValueChange={handleTabChange}>
            <div className="border-b">
              <TabsList className="w-full justify-start rounded-none bg-transparent h-auto p-0">
                <TabsTrigger
                  value="outbound"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                >
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    <span>出库管理</span>
                    {statistics?.by_status?.pending > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {statistics.by_status.pending}
                      </Badge>
                    )}
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="inbound"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                >
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    <span>入库管理</span>
                  </div>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="outbound" className="p-6 mt-0">
              <div className="flex justify-end mb-4">
                <Button
                  variant="secondary"
                  onClick={() => handleExport('outbound')}
                  disabled={loading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  导出出库单
                </Button>
              </div>
              <OutboundList
                refreshTrigger={refreshTrigger}
                canConfirm={canConfirmMovement}
                onRefresh={handleRefresh}
              />
            </TabsContent>

            <TabsContent value="inbound" className="p-6 mt-0">
              <div className="flex justify-end mb-4">
                <Button
                  variant="secondary"
                  onClick={() => handleExport('inbound')}
                  disabled={loading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  导出入库单
                </Button>
              </div>
              <InboundList
                refreshTrigger={refreshTrigger}
                onRefresh={handleRefresh}
              />
            </TabsContent>
          </Tabs>
        </Card>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}

        {/* Create Outbound Dialog */}
        <CreateOutboundDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateSuccess}
        />

        {/* Create Inbound Dialog */}
        <CreateInboundDialog
          open={createInboundDialogOpen}
          onClose={() => setCreateInboundDialogOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      </div>
    </Layout>
  );
}

export default WarehouseManagementPage;
